import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Input,
  Button,
  useColorModeValue,
  Spinner,
  Center,
  Icon,
  Code,
  Collapse,
  IconButton,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiDownload } from 'react-icons/fi';
import moment from 'moment';
import { supabase, isSupabaseConfigured, transformAuditLogToCamelCase } from '../../../utils/supabase';

const ACTION_LABELS = {
  // Contact actions
  'contact_created': 'Contact Created',
  'contact_updated': 'Contact Updated',
  'contact_deleted': 'Contact Deleted',
  'contact_merged': 'Contacts Merged',
  
  // Deal actions
  'deal_created': 'Deal Created',
  'deal_updated': 'Deal Updated',
  'deal_deleted': 'Deal Deleted',
  'deal_ownership_change': 'Ownership Changed',
  'deal_reassignment': 'Deal Reassigned',
  'stage_change': 'Stage Changed',
  
  // Bulk operations
  'bulk_stage_update': 'Bulk Stage Update',
  'bulk_tag_update': 'Bulk Tag Update',
  'bulk_assignment': 'Bulk Assignment',
  'bulk_delete': 'Bulk Delete',
  'bulk_contact_update': 'Bulk Contact Update',
  
  // Task operations
  'task_created': 'Task Created',
  'task_completed': 'Task Completed',
  'task_reassigned': 'Task Reassigned',
  
  // Template operations
  'template_created': 'Template Created',
  'template_updated': 'Template Updated',
  'template_deleted': 'Template Deleted',
  
  // Import operations
  'csv_import': 'CSV Import',
  'contacts_imported': 'Contacts Imported',
  'deals_imported': 'Deals Imported',
  
  // Settings
  'settings_changed': 'CRM Settings Changed',
  'user_role_change': 'User Role Changed',
  
  // Integration
  'webhook_lead_received': 'Webhook Lead',
};

const ACTION_COLORS = {
  // Contact actions
  'contact_created': 'green',
  'contact_updated': 'blue',
  'contact_deleted': 'red',
  'contact_merged': 'purple',
  
  // Deal actions
  'deal_created': 'green',
  'deal_updated': 'blue',
  'deal_deleted': 'red',
  'deal_ownership_change': 'blue',
  'deal_reassignment': 'cyan',
  'stage_change': 'green',
  
  // Bulk operations
  'bulk_stage_update': 'purple',
  'bulk_tag_update': 'teal',
  'bulk_assignment': 'blue',
  'bulk_delete': 'red',
  'bulk_contact_update': 'purple',
  
  // Task operations
  'task_created': 'green',
  'task_completed': 'green',
  'task_reassigned': 'orange',
  
  // Template operations
  'template_created': 'green',
  'template_updated': 'yellow',
  'template_deleted': 'red',
  
  // Import operations
  'csv_import': 'purple',
  'contacts_imported': 'purple',
  'deals_imported': 'purple',
  
  // Settings
  'settings_changed': 'orange',
  'user_role_change': 'orange',
  
  // Integration
  'webhook_lead_received': 'cyan',
};

export default function AuditLogViewer() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('7days');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'timeline'

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const expandedBG = useColorModeValue('blue.50', 'blue.900');

  useEffect(() => {
    loadAuditLogs();
  }, [actionFilter, userFilter, dateFilter, entityTypeFilter]);

  const loadAuditLogs = async () => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Build query
      let query = supabase
        .from('crm_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      // Filter by action type
      if (actionFilter) {
        query = query.eq('action', actionFilter);
      }

      // Filter by user
      if (userFilter) {
        query = query.eq('user_id', userFilter);
      }

      // Filter by entity type
      if (entityTypeFilter) {
        query = query.eq('entity_type', entityTypeFilter);
      }

      // Filter by date
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate;

        switch (dateFilter) {
          case '24hours':
            startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7days':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30days':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }

        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      let logsList = (data || []).map(transformAuditLogToCamelCase);

      // Apply client-side search filter
      if (searchTerm.trim() || sourceFilter.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const sourceLower = sourceFilter.toLowerCase();
        
        logsList = logsList.filter(log => {
          const matchesSearch = !searchTerm.trim() || (
            log.description?.toLowerCase().includes(searchLower) ||
            log.userName?.toLowerCase().includes(searchLower) ||
            log.actionLabel?.toLowerCase().includes(searchLower) ||
            log.entityId?.toLowerCase().includes(searchLower) ||
            JSON.stringify(log.details).toLowerCase().includes(searchLower)
          );

          const matchesSource = !sourceFilter.trim() || (
            (log.details?.source && log.details.source.toLowerCase().includes(sourceLower)) ||
            (log.metadata?.source && log.metadata.source.toLowerCase().includes(sourceLower))
          );

          return matchesSearch && matchesSource;
        });
      }

      setLogs(logsList);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (logId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleExportLogs = () => {
    // Export visible logs to CSV
    const csvData = logs.map((log) => ({
      Timestamp: moment(log.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      Action: ACTION_LABELS[log.action] || log.action,
      User: log.userName,
      Details: JSON.stringify(log.details),
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map((row) => Object.values(row).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <Center py={12}>
        <VStack>
          <Spinner size="lg" />
          <Text>Loading audit logs...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Audit Logs</Heading>
            <Text fontSize="sm" color="gray.600">
              Track all critical actions in your CRM
            </Text>
          </VStack>
          <Button
            leftIcon={<FiDownload />}
            size="sm"
            variant="outline"
            onClick={handleExportLogs}
            isDisabled={logs.length === 0}
          >
            Export to CSV
          </Button>
        </HStack>

        {/* View Mode Toggle */}
        <HStack spacing={2}>
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'solid' : 'outline'}
            colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
            onClick={() => setViewMode('table')}
          >
            Table View
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'timeline' ? 'solid' : 'outline'}
            colorScheme={viewMode === 'timeline' ? 'blue' : 'gray'}
            onClick={() => setViewMode('timeline')}
          >
            Timeline View
          </Button>
        </HStack>

        {/* Filters */}
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
            maxW="200px"
          />

          <Select
            placeholder="All Actions"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            size="sm"
            maxW="200px"
          >
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>

          <Select
            placeholder="All Entities"
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            size="sm"
            maxW="150px"
          >
            <option value="contact">Contacts</option>
            <option value="deal">Deals</option>
            <option value="task">Tasks</option>
            <option value="template">Templates</option>
          </Select>

          <Input
            placeholder="Filter by Source..."
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            size="sm"
            maxW="150px"
          />

          <Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            size="sm"
            maxW="150px"
          >
            <option value="24hours">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </Select>

          {(actionFilter || entityTypeFilter || sourceFilter || dateFilter !== '7days' || searchTerm) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setActionFilter('');
                setEntityTypeFilter('');
                setSourceFilter('');
                setDateFilter('7days');
                setSearchTerm('');
              }}
            >
              Clear All
            </Button>
          )}
        </HStack>

        {/* Audit Log Table */}
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBG}>
              <Tr>
                <Th width="50px"></Th>
                <Th>Timestamp</Th>
                <Th>Action</Th>
                <Th>User</Th>
                <Th>Summary</Th>
              </Tr>
            </Thead>
            <Tbody>
              {logs.length === 0 ? (
                <Tr>
                  <Td colSpan={5}>
                    <Center py={8}>
                      <Text color="gray.500">No audit logs found</Text>
                    </Center>
                  </Td>
                </Tr>
              ) : (
                logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <Tr _hover={{ bg: hoverBG }}>
                      <Td>
                        <IconButton
                          icon={expandedRows.has(log.id) ? <FiChevronUp /> : <FiChevronDown />}
                          size="xs"
                          variant="ghost"
                          onClick={() => toggleRow(log.id)}
                          aria-label="Toggle details"
                        />
                      </Td>
                      <Td fontSize="xs" fontFamily="mono">
                        {moment(log.createdAt).format('MM/DD/YY HH:mm:ss')}
                      </Td>
                      <Td>
                        <Badge colorScheme={ACTION_COLORS[log.action] || 'gray'} fontSize="xs">
                          {ACTION_LABELS[log.action] || log.action}
                        </Badge>
                      </Td>
                      <Td fontSize="sm">{log.userName || 'System'}</Td>
                      <Td fontSize="sm">
                        {log.description || (
                          <>
                            {log.details?.dealCount && `${log.details.dealCount} deals`}
                            {log.details?.contactCount && `${log.details.contactCount} contacts`}
                            {log.details?.dealName && log.details.dealName}
                            {log.details?.contactName && log.details.contactName}
                            {log.details?.newStage && ` → ${log.details.newStage}`}
                            {log.details?.assignedTo && ` → ${log.details.assignedTo}`}
                          </>
                        )}
                      </Td>
                    </Tr>
                    <Tr>
                      <Td colSpan={5} p={0}>
                        <Collapse in={expandedRows.has(log.id)}>
                          <Box bg={expandedBG} p={4} borderTopWidth="1px" borderColor={borderColor}>
                            <VStack align="stretch" spacing={3}>
                              {/* Description */}
                              {log.description && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={1}>
                                    Description:
                                  </Text>
                                  <Text fontSize="sm">{log.description}</Text>
                                </Box>
                              )}

                              {/* Entity Info */}
                              {log.entityType && log.entityId && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={1}>
                                    Entity:
                                  </Text>
                                  <HStack>
                                    <Badge colorScheme="purple">{log.entityType}</Badge>
                                    <Code fontSize="xs">{log.entityId}</Code>
                                  </HStack>
                                </Box>
                              )}

                              {/* Before/After Diff */}
                              {log.beforeSnapshot && log.afterSnapshot && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                                    Changes:
                                  </Text>
                                  <VStack align="stretch" spacing={2}>
                                    {Object.keys(log.afterSnapshot).map(key => {
                                      const before = log.beforeSnapshot[key];
                                      const after = log.afterSnapshot[key];
                                      
                                      if (JSON.stringify(before) === JSON.stringify(after)) {
                                        return null;
                                      }

                                      return (
                                        <Box key={key} p={2} bg="white" borderRadius="md" fontSize="xs">
                                          <Text fontWeight="semibold" mb={1}>{key}:</Text>
                                          <HStack spacing={2}>
                                            <Badge colorScheme="red" variant="subtle">Before: {JSON.stringify(before)}</Badge>
                                            <Text>→</Text>
                                            <Badge colorScheme="green" variant="subtle">After: {JSON.stringify(after)}</Badge>
                                          </HStack>
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                </Box>
                              )}

                              {/* Details */}
                              <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                                  Details:
                                </Text>
                                <Code fontSize="xs" p={2} borderRadius="md" display="block" whiteSpace="pre-wrap">
                                  {JSON.stringify(log.details, null, 2)}
                                </Code>
                              </Box>

                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold" mb={1}>
                                    Metadata:
                                  </Text>
                                  <Code fontSize="xs" p={2} borderRadius="md" display="block" whiteSpace="pre-wrap">
                                    {JSON.stringify(log.metadata, null, 2)}
                                  </Code>
                                </Box>
                              )}
                            </VStack>
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  </React.Fragment>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        {logs.length === 100 && (
          <Text fontSize="sm" color="gray.600" textAlign="center">
            Showing most recent 100 logs. Use filters to narrow results.
          </Text>
        )}
      </VStack>
    </Box>
  );
}

